type SoundType = 'nextRound' | 'press' | 'lose' | 'match';

let isInGamePageFlag = false;

let openGameMenuCallback: (() => void) | null = null;

let isInFadeOutProcess = false;

export const setNavigatingBetweenGames = (value: boolean) => {};

export const setIsInGamePage = (value: boolean) => {
  isInGamePageFlag = value;
};

export const isInGamePage = (): boolean => {
  return isInGamePageFlag;
};

export const setOpenGameMenuCallback = (callback: () => void) => {
  openGameMenuCallback = callback;
};

class SoundManager {
  private static instance: SoundManager;
  private sounds: Map<SoundType, HTMLAudioElement>;
  private backgroundMusic: HTMLAudioElement | null = null;
  private enabled: boolean = true;
  private soundEffectsEnabled: boolean = true;
  private musicEnabled: boolean = true;
  private soundsLoaded: boolean = false;
  private musicLoaded: boolean = false;
  private fadeInterval: number | null = null;
  private defaultMusicVolume: number = 1;
  private hasUserInteracted: boolean = false;
  private pendingMusicPlay: boolean = false;
  private isPlaying: boolean = false;
  private playTimeout: NodeJS.Timeout | null = null;
  private shouldPlayOnVisibilityChange: boolean = false;
  private soundDebounceTimers: Map<SoundType, number> = new Map();
  private soundDebounceTime: number = 50;
  private musicDebounceTime: number = 300;
  private lastMusicPlayTime: number = 0;

  private soundVolumes: Record<SoundType, number> = {
    match: 0.4,
    lose: 0.2,
    press: 1,
    nextRound: 0.4,
  };

  private constructor() {
    this.sounds = new Map();
    this.initSounds();
    this.initBackgroundMusic();

    if (
      document.readyState === 'interactive' ||
      document.readyState === 'complete'
    ) {
      this.hasUserInteracted = true;
    }

    this.setupUserInteractionListener();
  }

  public markUserInteraction(): void {
    this.hasUserInteracted = true;
  }

  private setupUserInteractionListener(): void {
    const interactionEvents = [
      'click',
      'touchstart',
      'keydown',
      'mousedown',
      'pointerdown',
    ];

    const handleUserInteraction = (event: Event) => {
      this.hasUserInteracted = true;

      if (this.musicEnabled && this.enabled && isInGamePageFlag) {
        this.clearPlayTimeout();
        this.playTimeout = setTimeout(() => {
          this.playBackgroundMusic('después de interacción');
          this.pendingMusicPlay = false;
        }, 100);
      }

      interactionEvents.forEach((event) => {
        document.removeEventListener(event, handleUserInteraction);
      });
    };

    interactionEvents.forEach((event) => {
      document.addEventListener(event, handleUserInteraction, {
        capture: true,
      });
    });

    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        if (!this.hasUserInteracted) {
          this.markUserInteracted();

          if (this.musicEnabled && this.enabled && isInGamePageFlag) {
            this.clearPlayTimeout();
            this.playTimeout = setTimeout(() => {
              this.playBackgroundMusic('después de visibilitychange');
              this.pendingMusicPlay = false;
            }, 100);
          }
        } else if (
          this.musicEnabled &&
          this.enabled &&
          this.backgroundMusic &&
          this.shouldPlayOnVisibilityChange &&
          isInGamePageFlag
        ) {
          if (this.backgroundMusic.paused) {
            this.backgroundMusic.volume = 0;
            this.backgroundMusic.play().catch(() => {});
          }

          this.isPlaying = true;
          this.clearFadeInterval();

          let volume = 0;
          this.fadeInterval = window.setInterval(() => {
            if (this.backgroundMusic) {
              volume += 0.05;
              if (volume >= this.defaultMusicVolume) {
                volume = this.defaultMusicVolume;
                this.clearFadeInterval();
              }
              this.backgroundMusic.volume = volume;
            }
          }, 100);
        }
      } else {
        if (isInGamePageFlag) {
          if (openGameMenuCallback) {
            openGameMenuCallback();
          }

          if (
            this.isPlaying &&
            this.backgroundMusic &&
            !this.backgroundMusic.paused
          ) {
            this.shouldPlayOnVisibilityChange = true;

            this.clearFadeInterval();
            this.fadeInterval = window.setInterval(() => {
              if (this.backgroundMusic && this.backgroundMusic.volume > 0.05) {
                this.backgroundMusic.volume -= 0.05;
              } else if (this.backgroundMusic) {
                this.backgroundMusic.volume = 0;
                this.backgroundMusic.pause();
                this.clearFadeInterval();
              }
            }, 100);
          }
        }
      }
    });

    window.addEventListener('beforeunload', (event) => {
      if (isInGamePageFlag && openGameMenuCallback) {
        openGameMenuCallback();
        event.preventDefault();
        event.returnValue = ''; // Esto mantiene el bloqueo con el diálogo genérico del navegador
      }
    });
  }

  public static getInstance(): SoundManager {
    if (!SoundManager.instance) {
      SoundManager.instance = new SoundManager();
    }
    return SoundManager.instance;
  }

  private initSounds(): void {
    this.loadSound('nextRound', '/sounds/next-round.mp3');
    this.loadSound('press', '/sounds/press.mp3');
    this.loadSound('lose', '/sounds/lose.mp3');
    this.loadSound('match', '/sounds/match.mp3');
    this.soundsLoaded = true;
  }

  private initBackgroundMusic(): void {
    this.backgroundMusic = new Audio('/sounds/music.mp3');
    if (this.backgroundMusic) {
      this.backgroundMusic.loop = true;
      this.backgroundMusic.volume = 0;
      this.defaultMusicVolume = 1;
      this.backgroundMusic.preload = 'auto';
      this.backgroundMusic.addEventListener('canplaythrough', () => {
        this.musicLoaded = true;
      });
    }
  }

  private loadSound(type: SoundType, path: string): void {
    const audio = new Audio(path);
    audio.preload = 'auto';
    audio.volume = this.soundVolumes[type];
    this.sounds.set(type, audio);
  }

  public setSoundVolume(type: SoundType, volume: number): void {
    if (volume >= 0 && volume <= 1) {
      this.soundVolumes[type] = volume;
      const sound = this.sounds.get(type);
      if (sound) {
        sound.volume = volume;
      }
    }
  }

  public getSoundVolume(type: SoundType): number {
    return this.soundVolumes[type];
  }

  public play(type: SoundType): void {
    if (!this.enabled) return;

    if (!this.soundEffectsEnabled) return;

    if (!this.soundsLoaded) return;

    const lastPlayTime = this.soundDebounceTimers.get(type) || 0;
    const currentTime = Date.now();

    if (currentTime - lastPlayTime < this.soundDebounceTime) {
      return;
    }

    this.soundDebounceTimers.set(type, currentTime);

    const sound = this.sounds.get(type);
    if (!sound) return;

    try {
      const soundClone = sound.cloneNode() as HTMLAudioElement;
      soundClone.volume = this.soundVolumes[type];

      if (!this.hasUserInteracted) return;

      const playPromise = soundClone.play();
      if (playPromise !== undefined) {
        playPromise.catch((error) => {});
      }
    } catch (error) {}
  }

  public playBackgroundMusic(gameName: string = ''): void {
    if (!this.musicEnabled) {
      return;
    }

    const currentTime = Date.now();
    if (currentTime - this.lastMusicPlayTime < this.musicDebounceTime) {
      return;
    }

    this.lastMusicPlayTime = currentTime;

    if (isInFadeOutProcess && isInGamePageFlag && this.backgroundMusic) {
      this.clearFadeInterval();
      isInFadeOutProcess = false;

      if (!this.backgroundMusic.paused) {
        this.isPlaying = true;

        let currentVolume = this.backgroundMusic.volume;
        this.fadeInterval = window.setInterval(() => {
          if (this.backgroundMusic) {
            currentVolume += 0.05;
            if (currentVolume >= this.defaultMusicVolume) {
              currentVolume = this.defaultMusicVolume;
              this.clearFadeInterval();
            }
            this.backgroundMusic.volume = currentVolume;
          }
        }, 50);
        return;
      }
    }

    if (
      !this.backgroundMusic ||
      !this.musicLoaded ||
      (this.isPlaying && !isInFadeOutProcess) ||
      !isInGamePageFlag
    ) {
      return;
    }

    if (!this.hasUserInteracted) {
      this.pendingMusicPlay = true;
      return;
    }

    this.clearFadeInterval();

    if (isInFadeOutProcess && this.backgroundMusic) {
      isInFadeOutProcess = false;
    } else {
      if (this.backgroundMusic) {
        this.backgroundMusic.volume = 0;
      }
    }

    this.isPlaying = true;

    try {
      const playPromise = this.backgroundMusic.play();
      if (playPromise !== undefined) {
        playPromise.catch((error) => {
          this.pendingMusicPlay = true;
          this.isPlaying = false;

          this.clearPlayTimeout();
          this.playTimeout = setTimeout(() => {
            if (
              this.musicEnabled &&
              this.backgroundMusic &&
              this.backgroundMusic.paused &&
              isInGamePageFlag
            ) {
              this.playBackgroundMusic('reintento después de error');
            }
          }, 1000);
        });
      }

      let volume = this.backgroundMusic.volume || 0;
      this.fadeInterval = window.setInterval(() => {
        if (this.backgroundMusic) {
          volume += 0.05;
          if (volume >= this.defaultMusicVolume) {
            volume = this.defaultMusicVolume;
            this.clearFadeInterval();
          }
          this.backgroundMusic.volume = volume;
        }
      }, 50);
    } catch (error) {
      this.pendingMusicPlay = true;
      this.isPlaying = false;

      this.clearPlayTimeout();
      this.playTimeout = setTimeout(() => {
        if (
          this.musicEnabled &&
          this.backgroundMusic &&
          this.backgroundMusic.paused &&
          isInGamePageFlag
        ) {
          this.playBackgroundMusic('reintento después de excepción');
        }
      }, 1000);
    }
  }

  public pauseBackgroundMusic(): void {
    if (this.backgroundMusic) {
      this.shouldPlayOnVisibilityChange = false;
      isInFadeOutProcess = true;
      this.clearFadeInterval();

      const fadeStep = 0.02;
      const fadeInterval = 50;

      this.fadeInterval = window.setInterval(() => {
        if (this.backgroundMusic && this.backgroundMusic.volume > fadeStep) {
          this.backgroundMusic.volume -= fadeStep;
        } else if (this.backgroundMusic) {
          this.backgroundMusic.volume = 0;
          this.backgroundMusic.pause();
          this.isPlaying = false;
          this.clearFadeInterval();
          isInFadeOutProcess = false;
        }
      }, fadeInterval);
    }
  }

  public fadeOutAndStopBackgroundMusic(): void {
    if (this.backgroundMusic) {
      this.shouldPlayOnVisibilityChange = false;
      this.pendingMusicPlay = false;
      isInFadeOutProcess = true;

      if (this.playTimeout) {
        clearTimeout(this.playTimeout);
        this.playTimeout = null;
      }

      this.clearFadeInterval();
      this.fadeInterval = window.setInterval(() => {
        if (this.backgroundMusic && this.backgroundMusic.volume > 0.05) {
          this.backgroundMusic.volume -= 0.05;
        } else if (this.backgroundMusic) {
          this.backgroundMusic.volume = 0;
          this.backgroundMusic.pause();
          this.backgroundMusic.currentTime = 0;
          this.isPlaying = false;
          this.shouldPlayOnVisibilityChange = false;
          this.clearFadeInterval();
          isInFadeOutProcess = false;
        }
      }, 100);
    }
  }

  public stopBackgroundMusic(): void {
    if (this.backgroundMusic) {
      this.shouldPlayOnVisibilityChange = false;
      isInFadeOutProcess = false;
      if (this.fadeInterval !== null) {
        clearInterval(this.fadeInterval);
        this.fadeInterval = null;
      }
      this.backgroundMusic.pause();
      this.backgroundMusic.currentTime = 0;
      this.isPlaying = false;
      if (this.playTimeout) {
        clearTimeout(this.playTimeout);
        this.playTimeout = null;
      }
    }
  }

  public setEnabled(enabled: boolean): void {
    this.enabled = enabled;

    if (!enabled && this.backgroundMusic) {
      this.pauseBackgroundMusic();
    } else if (
      enabled &&
      this.musicEnabled &&
      this.backgroundMusic &&
      this.musicLoaded &&
      isInGamePageFlag
    ) {
      this.playBackgroundMusic('después de activar SoundManager');
    }
  }

  public setMusicEnabled(enabled: boolean): void {
    this.musicEnabled = enabled;
    if (!enabled && this.backgroundMusic) {
    } else if (
      enabled &&
      this.enabled &&
      this.backgroundMusic &&
      this.musicLoaded &&
      isInGamePageFlag
    ) {
      this.playBackgroundMusic('después de habilitar música');
    }
  }

  public setSoundEffectsEnabled(enabled: boolean): void {
    this.soundEffectsEnabled = enabled;

    if (enabled && !this.enabled) {
      this.enabled = true;
    }

    if (enabled && !this.soundsLoaded) {
      this.initSounds();
    }
  }

  public isEnabled(): boolean {
    return this.enabled;
  }

  public isMusicEnabled(): boolean {
    return this.musicEnabled;
  }

  public isSoundEffectsEnabled(): boolean {
    return this.soundEffectsEnabled;
  }

  public areSoundsLoaded(): boolean {
    return this.soundsLoaded;
  }

  public isMusicLoaded(): boolean {
    return this.musicLoaded;
  }

  private clearFadeInterval(): void {
    if (this.fadeInterval !== null) {
      clearInterval(this.fadeInterval);
      this.fadeInterval = null;
    }
  }

  private clearPlayTimeout(): void {
    if (this.playTimeout !== null) {
      clearTimeout(this.playTimeout);
      this.playTimeout = null;
    }
  }
}

export const soundManager = SoundManager.getInstance();

export const markUserInteraction = () => soundManager.markUserInteraction();
export const playNextRoundSound = () => soundManager.play('nextRound');
export const playPressSound = () => soundManager.play('press');
export const playLoseSound = () => soundManager.play('lose');
export const playMatchSound = () => soundManager.play('match');
export const playBackgroundMusic = (gameName: string = '') => {
  if (!soundManager.isMusicEnabled()) {
    return;
  }
  soundManager.playBackgroundMusic(gameName);
};
export const pauseBackgroundMusic = () => soundManager.pauseBackgroundMusic();
export const stopBackgroundMusic = () => soundManager.stopBackgroundMusic();
export const fadeOutAndStopBackgroundMusic = () =>
  soundManager.fadeOutAndStopBackgroundMusic();
export const setSoundVolume = (type: SoundType, volume: number) =>
  soundManager.setSoundVolume(type, volume);
export const getSoundVolume = (type: SoundType) =>
  soundManager.getSoundVolume(type);

export const playPressSoundDirectly = () => {
  try {
    const pressSound = new Audio('/sounds/press.mp3');

    const volume = soundManager.getSoundVolume('press') || 1;
    pressSound.volume = volume;

    const playPromise = pressSound.play();
    if (playPromise !== undefined) {
      playPromise.catch((error) => {});
    }
  } catch (error) {}

  markUserInteraction();
};

export default soundManager;
